import { ReactElement, useMemo, useRef } from 'react';

import {
   DocplannerDoctorAddressMapping,
   HealthcareProviderLocation as DocplannerHealthcareProviderLocation,
} from '@dreryk/egabinet-docplanner-frontend-api';
import { HealthcareProviderLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import {
   createDynamicColumnsWidth,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { docplannerTranslationKeys, doctorsMappingsTranslationKeys } from '../../../translations';
import { FacilityDescription } from '../FacilityDescription';
import { FacilityDescriptionCell } from '../FacilityDescriptionCell';

import { AddDoctorAssociationFormBean, AddDoctorAssociationFormController } from './addDoctorAssociation.types';
import { HealthcareProviderLocationSelectCell } from './HealthcareProviderLocationSelectCell/HealthcareProviderLocationSelectCell';

type FacilitiesAssociationsInteractiveTableProps = {
   readonly register: AddDoctorAssociationFormController['register'];
};

const mapDocplannerHealthcareProviderLocationToCommonHealthcareProvider = (
   docplannerHealthcareProviderLocation: DocplannerHealthcareProviderLocation
): HealthcareProviderLocation => {
   return {
      address: docplannerHealthcareProviderLocation.address,
      locationIdentifier: {
         type: docplannerHealthcareProviderLocation.type,
         locationId: docplannerHealthcareProviderLocation.locationId,
      },
      name: docplannerHealthcareProviderLocation.name,
   };
};

const useColumns = (): InteractiveTableColumnConfiguration<DocplannerDoctorAddressMapping>[] => {
   const { current: columnWidths } = useRef(
      createDynamicColumnsWidth({
         docplannerInstitutionName: '50%',
         eGabinetServiceProvidingLocationAssociation: '50%',
      })
   );
   return useMemo<InteractiveTableColumnConfiguration<DocplannerDoctorAddressMapping>[]>(
      () => [
         {
            name: 'externalName',
            label: docplannerTranslationKeys.docplannerInstitutionName,

            renderer: ({ bean, index }: ValueRendererProps<DocplannerDoctorAddressMapping>) => {
               return (
                  <FacilityDescriptionCell index={index} externalAddressId={bean.externalAddressId}>
                     <FacilityDescription
                        name={bean.externalName}
                        address={`${bean.externalStreetName} ${bean.externalPostCode}`}
                     />
                  </FacilityDescriptionCell>
               );
            },
            width: columnWidths.docplannerInstitutionName,
            sortingDisabled: true,
         },
         {
            name: 'serviceProvidingLocation',
            label: doctorsMappingsTranslationKeys.egabinetMUS,

            renderer: ({ bean, index }: ValueRendererProps<DocplannerDoctorAddressMapping>) => {
               return (
                  <HealthcareProviderLocationSelectCell
                     index={index}
                     defaultValue={
                        bean.healthcareProviderLocation
                           ? mapDocplannerHealthcareProviderLocationToCommonHealthcareProvider(
                                bean.healthcareProviderLocation
                             )
                           : undefined
                     }
                  />
               );
            },
            width: columnWidths.eGabinetServiceProvidingLocationAssociation,
         },
      ],
      [columnWidths]
   );
};

export const FacilitiesAssociationsInteractiveTable = (
   props: FacilitiesAssociationsInteractiveTableProps
): ReactElement => {
   return (
      <InteractiveTable<DocplannerDoctorAddressMapping, AddDoctorAssociationFormBean>
         testId={'docplannerDoctorAddressMappings'}
         columns={useColumns()}
         name={'docplannerDoctorAddressMappings'}
         register={props.register}
      />
   );
};
